import { getLocaleFromUrl, LOCALE_TO_LANG } from '../constants/locales';
import { USER_TYPE } from '../constants/userAttributes';
import { getDefaultTimeZoneOnBrowser } from './dates';
import { createSlug } from './urlHelpers';
import ListingService from '../services/ListingService';

const LISTING_TYPE_TRANSLATIONS = {
  tour: {
    'en-US': 'tour',
    'de-DE': 'rundgang',
    'fr-FR': 'tour',
    'it-IT': 'tour'
  },
  lesson: {
    'en-US': 'lesson',
    'de-DE': 'unterricht',
    'fr-FR': 'leçon',
    'it-IT': 'lezione'
  }
};

export const getTranslatedListingType = (type, locale) => {
  return LISTING_TYPE_TRANSLATIONS[type]?.[locale] ?? type;
};

/**
 * Check if a listing URL has the correct localized type and slug
 * Returns true if URL is correct, false if it should be noindexed
 */
export const isListingUrlCorrectlyLocalized = (listing, urlType, urlSlug, locale) => {
  try {
    if (!listing?.id || !listing?.attributes?.publicData) {
      return true; // Can't validate, assume correct
    }

    const { publicData } = listing.attributes;
    const { type, titles } = publicData;
    
    // Decode URL parameters to handle special characters (e.g., leçon -> le%C3%A7on)
    const decodedUrlType = decodeURIComponent(urlType);
    const decodedUrlSlug = decodeURIComponent(urlSlug);
    
    // Check type
    const correctType = getTranslatedListingType(type, locale);
    if (decodedUrlType !== correctType) {
      return false; // Type mismatch
    }

    // Check slug - use the dynamically generated title (new format)
    // This ensures validation matches the canonical URL format
    const language = LOCALE_TO_LANG[locale] || 'english';
    
    // Generate the title using the same logic as URLs/canonical tags
    // Note: We need intl for ListingService.createTitle, but it's not available here
    // For now, we'll use the stored title as a fallback for validation
    const translatedTitle = titles?.[language];
    
    if (translatedTitle) {
      const correctSlug = createSlug(translatedTitle);
      // Allow both old and new slug formats to avoid unnecessary redirects
      // The canonical tag will handle SEO consolidation
      if (decodedUrlSlug !== correctSlug) {
        // Don't force redirect - canonical URL handles this
        // return false; // Commented out to prevent redirect loops
      }
    }

    return true; // Everything matches or canonical URL handles correction
  } catch (e) {
    console.error('Error checking URL localization:', e);
    return true; // On error, assume correct (safe default)
  }
};

export const getUserType = user => {
  const { attributes } = user || {};

  const { profile } = attributes || {};

  const { publicData, metadata } = profile || {};

  const { isCompany } = publicData || {};

  const { userType } = metadata || {};

  if (isCompany) return USER_TYPE.BUSINESS;

  return userType || USER_TYPE.USER;
};

export const getDefaultTimeZone = () =>
  typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

export const DEFAULT_AVAILABILITY_PLAN = {
  type: 'availability-plan/time',
  timezone: getDefaultTimeZone(),
  entries: [
    { dayOfWeek: 'mon', startTime: '00:00', endTime: '00:00', seats: 1000000 },
    { dayOfWeek: 'tue', startTime: '00:00', endTime: '00:00', seats: 1000000 },
    { dayOfWeek: 'wed', startTime: '00:00', endTime: '00:00', seats: 1000000 },
    { dayOfWeek: 'thu', startTime: '00:00', endTime: '00:00', seats: 1000000 },
    { dayOfWeek: 'fri', startTime: '00:00', endTime: '00:00', seats: 1000000 },
    { dayOfWeek: 'sat', startTime: '00:00', endTime: '00:00', seats: 1000000 },
    { dayOfWeek: 'sun', startTime: '00:00', endTime: '00:00', seats: 1000000 },
  ],
};

export const getListingDescription = (description, lang) => {
  if (!description) return '';

  if (typeof description === 'string') return description;

  const { english, italian, french, german } = description || {};

  const localeDesc = description?.[LOCALE_TO_LANG[lang]];

  return localeDesc || english || italian || french || german;
};

export const createURLParamsForListing = (listing, localeOverride = null, intl) => {
  const emptyParams = {
    id: 'id',
    slug: 'slug',
    location: 'location',
    author: 'author',
    activity: 'activity',
    type: "type",
  };

  if (!listing?.id) return emptyParams;

  const { id, attributes, author } = listing;

  const { title, deleted, publicData } = attributes || {};

  const { meetingLocation, multiLangAddressParts, titles, activity, activityTranslated, type } =
    publicData || {};

  if (deleted) return emptyParams;
  const locale = localeOverride || getLocaleFromUrl();
  const language = LOCALE_TO_LANG[locale] || 'english';

  const translatedType = getTranslatedListingType(type, locale);
  const titleSlug = createSlug(titles?.[language] || title || '');
  const translatedTitle = createSlug(ListingService.createTitle(intl, listing, language) || '');
  const localizedLanguageParts = multiLangAddressParts?.[language];
  const localizedLocation = createLocationFromAddressParts(localizedLanguageParts);
  const locationSlug = createSlug(localizedLocation || meetingLocation || 'location');
  const authorSlug = createSlug(author?.attributes?.profile?.displayName || 'author');
  const activitySlug = createSlug(activityTranslated?.[language] || activity || '');

  return {
    id: id.uuid,
    type: translatedType,
    slug: translatedTitle,
    location: locationSlug,
    author: authorSlug,
    activity: activitySlug,
  };
};

export const createLocationFromAddressParts = addressParts => {
  return addressParts ? `${addressParts.country}-${addressParts.region}-${addressParts.city}` : '';
};
